import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import { IVideo } from '@models/IVideo';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import usePreviewMode from '@utils/hooks/usePreviewMode';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { useMemo, useRef, useState } from 'react';

interface Props {
  readonly title: string;
  readonly text?: string;
  readonly media: IVideo | IImage;
}

const KeyholeVideo = ({ title, text, media }: Props) => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [textContainerRef, { clientHeight: textContainerHeight }] =
    useResizeObserver();
  const [mediaRef, { height: mediaHeight }] = useResizeObserver();
  const isMobile = useMediaQuery(1024);
  const isPreview = usePreviewMode();
  const [mediaButtonFocusedByTab, setMediaButtonFocusedByTab] = useState(false);

  const mediaOffset = useMemo(
    () => textContainerHeight - mediaHeight * 0.2,
    [textContainerHeight, mediaHeight]
  );

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });

  const smoothScrollProgress = useSpring(scrollYProgress, {
    stiffness: 500,
    damping: 50,
    mass: 1,
  });

  const textTranslateY = useTransform(
    smoothScrollProgress,
    [0, 0.3],
    [0, isPreview ? 0 : textContainerHeight * 0.4]
  );

  const videoTranslateY = useTransform(
    smoothScrollProgress,
    [0, 0.3],
    [mediaOffset, isPreview ? mediaOffset : 0]
  );

  const mobileClipPath = [
    'inset(22.5% 5% 22.5% 5%)',
    'inset(10% 2.5% 10% 2.5%)',
    'inset(0% 0% 0% 0%)',
  ];

  const responsiveClipPath = isMobile
    ? mobileClipPath
    : ['inset(20%)', 'inset(10%)', 'inset(0%)'];

  const accessibileClipPath = mediaButtonFocusedByTab
    ? ['inset(0%)', 'inset(0%)', 'inset(0%)']
    : responsiveClipPath;

  const clipPath = useTransform(
    smoothScrollProgress,
    [0.3, 0.4, 0.7],
    isPreview ? ['inset(20%)', 'inset(20%)', 'inset(20%)'] : accessibileClipPath
  );

  const textOpacity = useTransform(scrollYProgress, (pos) => {
    if (isPreview || mediaButtonFocusedByTab) return 1;
    return pos >= 0.4 ? 0 : 1;
  });

  return (
    <section
      className={classNames(
        'component-padding relative bg-white text-black-100 dark:bg-black-100 dark:text-white',
        isPreview ? 'h-[1080px]' : 'h-[300vh] md:h-[450vh]'
      )}
      ref={sectionRef}
      data-testid="CT-154-Keyhole-Video"
    >
      <div
        className={classNames(
          'sticky top-0 w-full',
          isPreview ? 'h-[1080px]' : 'h-screen'
        )}
      >
        <motion.div
          className="absolute left-1/2 flex w-full flex-col items-center gap-5 px-10 py-10 text-center md:w-3/5 md:py-20 xl:w-1/2 xl:gap-7.5"
          style={{
            translateY: textTranslateY,
            translateX: '-50%',
            opacity: textOpacity,
          }}
          ref={textContainerRef}
        >
          <h1 data-testid="CT-154-Keyhole-Video-Title">{title}</h1>
          {text && (
            <p className="h6" data-testid="CT-154-Keyhole-Video-Text">
              {text}
            </p>
          )}
        </motion.div>

        <motion.div
          className="h-full focus:outline-none"
          ref={mediaRef}
          style={{
            clipPath,
            translateY: videoTranslateY,
          }}
          tabIndex={0}
        >
          {'url' in media && media.url ? (
            <InViewVideo
              hasInVideoPlayButton
              controls={false}
              video={media as IVideo}
              autoPlay
              inViewThreshold={0.1}
              onFocusCallback={(e) =>
                setMediaButtonFocusedByTab(e.target.matches(':focus-visible'))
              }
              videoRatio={isMobile ? 'aspect-2/3' : 'aspect-video'}
              videoClasses="[&>div>video]:object-cover h-full"
              wrapperClasses={classNames(
                'h-full transition duration-500 ease-in-out',
                isMobile === undefined ? 'opacity-0' : 'opacity-100'
              )}
              dataTestId="CT-154-Keyhole-Video-Inline-Video"
            />
          ) : (
            <Image
              image={media as IImage}
              imageClasses={classNames(
                'object-cover h-full w-full max-lg:aspect-2/3 lg:aspect-video',
                isMobile === undefined ? 'opacity-0' : 'opacity-100'
              )}
              dataTestId="CT-154-Keyhole-Video-Image"
            />
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default KeyholeVideo;
